import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { useAppForm } from '~/components/Form';
import {
  RoutePreferenceValue,
  SaveRoutePreferenceVariables,
} from '~/hooks/api/useRoutePreferences';
import Spacings from '~/modules/ui-kit/spacings';
import messages from './messages';

export type RoutePreferenceRow = {
  routeId: number;
  name: string;
  value: RoutePreferenceValue;
};

type RoutePreferenceSettingsFormProps = {
  rows: RoutePreferenceRow[];
  pendingRouteId?: number;
  onChange: (variables: SaveRoutePreferenceVariables) => Promise<void>;
};

const valueSchema = z.enum(['preferred', 'neutral', 'disfavored']);
const rowSchema = z.object({ value: valueSchema });

const ROUTE_PREFERENCE_OPTIONS = [
  { value: 'preferred', label: <FormattedMessage {...messages.preferred} /> },
  { value: 'neutral', label: <FormattedMessage {...messages.neutral} /> },
  { value: 'disfavored', label: <FormattedMessage {...messages.disfavored} /> },
];

type RowFormProps = {
  row: RoutePreferenceRow;
  disabled: boolean;
  onChange: (variables: SaveRoutePreferenceVariables) => Promise<void>;
};

const RowForm = ({ row, disabled, onChange }: RowFormProps) => {
  const form = useAppForm({
    defaultValues: { value: row.value },
    validators: { onChange: rowSchema },
    listeners: {
      onChange: ({ formApi }) => {
        if (formApi.state.isValid) {
          formApi.handleSubmit();
        }
      },
    },
    onSubmit: async ({ formApi, value }) => {
      if (value.value === row.value) return;
      await onChange({ routeId: row.routeId, value: value.value }).catch(
        () => {}
      );
      // Re-sync after the mutation settles. Same shape as the TanStack Form
      // react-query example. The cache holds the confirmed value on success
      // or the reverted value on error; reset snaps the form to it.
      if (formApi.state.values.value !== row.value) {
        formApi.reset({ value: row.value });
      }
    },
  });

  return (
    <form.AppField name="value">
      {(field) => (
        <field.RadioGroupField
          label={<Typography variant="h6">{row.name}</Typography>}
          disabled={disabled}
          options={ROUTE_PREFERENCE_OPTIONS}
        />
      )}
    </form.AppField>
  );
};

export const RoutePreferenceSettingsForm = (
  props: RoutePreferenceSettingsFormProps
) => (
  <Spacings.Inset>
    <Spacings.Stack scale="l">
      <Typography variant="h6">
        <FormattedMessage {...messages.routePreferenceSettings} />
      </Typography>
      <Typography color="textSecondary">
        <FormattedMessage {...messages.routePreferenceDescription} />
      </Typography>
      {props.rows.map((row) => (
        <RowForm
          key={row.routeId}
          row={row}
          disabled={props.pendingRouteId === row.routeId}
          onChange={props.onChange}
        />
      ))}
    </Spacings.Stack>
  </Spacings.Inset>
);
